import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCardContext } from '../../../context/card-context/card-context';
import { RotateControls } from '../../../features/card-editor/components/rotate-controls/rotate-controls';
import { usePhotoControls } from '../../../features/card-editor/hooks/use-photo-controls';
import { isPhotozoneImage } from '../../../features/card-editor/utils/is-photozone-image';
import { CustomFabricObject } from '../../../global-types/canvas';
import { useActiveCanvas, useFeatureFlags } from '../../../hooks';
import { Drawer } from '../drawer/drawer';
import { DrawerPositions } from '../drawer/drawer-types';
import { HeaderContent, FooterContent } from './fragments';
import { RotateDrawerProps } from './rotate-drawer-types';
import { getComputedRotation } from './rotate-drawer-utils';
import styles from './rotate-drawer.module.scss';
import { Text } from '@dotcom/crown-ui';

// The factor in which we want to scale the image when needs
// to be resized for it to be bigger than the photoZone.
// In this case is 1.1 because I want the image to be 10% bigger
// than the photoZone.
const SCALE_IMAGE_FACTOR = 1.1;

export const RotateDrawer = ({
  isOpen,
  onDrawerCancel,
  onDrawerApply,
  onChangeCallback,
}: RotateDrawerProps): React.ReactElement => {
  const { t } = useTranslation();
  const canvas = useActiveCanvas();
  const { IS_FORCE_FILL_ENABLED } = useFeatureFlags();
  const { rotateSelectedPhoto } = usePhotoControls();
  const { cardState } = useCardContext();
  const { cardFacesList, activeCardIndex } = cardState;
  const currentCardFace = cardFacesList[`${activeCardIndex}`];
  const isFrontFace = currentCardFace.type === 'front' && currentCardFace.active === true;
  const rotateValue = isFrontFace ? 90 : 45;
  const activeObject = canvas?.current?.getActiveObject();
  const originalObjectRef = useRef<CustomFabricObject | null>(null);
  const currentAngle = activeObject?.angle ?? 0;
  const handleCancel = () => {
    if (activeObject && originalObjectRef.current) {
      activeObject.set('scaleX', originalObjectRef.current.scaleX);
      activeObject.set('scaleY', originalObjectRef.current.scaleY);
      rotateSelectedPhoto(getComputedRotation(originalObjectRef.current.angle || 0));
      activeObject.left = originalObjectRef.current.left;
      activeObject.top = originalObjectRef.current.top;
      activeObject.setCoords();
      canvas?.current?.renderAll();
    }
    onDrawerCancel();
  };

  const onRotateClick = (rotateRight: boolean) => {
    if (activeObject && isPhotozoneImage(activeObject)) {
      const photozoneId = activeObject?.data?.photoZoneId;
      const photozoneObject = canvas?.current?.getObjects().find((obj) => obj.name === photozoneId);
      if (photozoneObject) {
        const imgWidth = activeObject.width as number;
        const imgHeight = activeObject.height as number;
        const zoneWidth = photozoneObject.width as number;
        const zoneHeight = photozoneObject.height as number;
        const imageScaleX = activeObject.scaleX ?? 1;
        const imageScaleY = activeObject.scaleY ?? 1;
        const imageRealWidth = imgWidth * imageScaleX;
        const imageRealHeight = imgHeight * imageScaleY;

        // If any image dimension is less than or equal to the zone, scale up to 10% bigger than the zone
        if (imageRealWidth <= zoneWidth || imageRealHeight <= zoneHeight) {
          // Calculate needed scale for 10% bigger than the zone
          const targetWidth = zoneWidth * SCALE_IMAGE_FACTOR;
          const targetHeight = zoneHeight * SCALE_IMAGE_FACTOR;
          // Calculate scale factors for both dimensions
          const neededScaleX = targetWidth / imgWidth;
          const neededScaleY = targetHeight / imgHeight;
          // Use the larger scale to ensure both dimensions are at least 10% bigger
          const neededScale = Math.max(neededScaleX, neededScaleY);
          activeObject.set({
            scaleX: neededScale,
            scaleY: neededScale,
          });
        }

        const photoZoneCenter = photozoneObject.getCenterPoint();
        activeObject.setPositionByOrigin(photoZoneCenter, 'center', 'center');
      }
    }
    rotateSelectedPhoto(
      Number(
        rotateRight
          ? getComputedRotation((activeObject?.angle || 0) + rotateValue)
          : getComputedRotation((activeObject?.angle || 0) - rotateValue),
      ),
    );
    if (onChangeCallback) {
      onChangeCallback();
    }
  };

  useEffect(() => {
    if (isOpen && activeObject) {
      originalObjectRef.current = activeObject.toObject();
    }
  }, [isOpen, activeObject]);

  return (
    <Drawer
      drawerPosition={DrawerPositions.Bottom}
      hideCloseButton={true}
      onClose={handleCancel}
      isOpen={isOpen}
      addClass={styles['rotate-drawer']}
      headerContent={
        IS_FORCE_FILL_ENABLED ? (
          <RotateControls onRotateLeft={() => onRotateClick(false)} onRotateRight={() => onRotateClick(true)} />
        ) : (
          <HeaderContent onChangeCallback={onChangeCallback} initialValue={currentAngle} />
        )
      }
      role="toolbar"
      showVeil={true}
      footerContent={<FooterContent onDrawerCancel={handleCancel} onDrawerApply={onDrawerApply} />}
    >
      <Text as="span" styleVariant="screen-reader">
        {t('rotateDrawer.rotateDrawer')}
      </Text>
    </Drawer>
  );
};

RotateDrawer.defaultProps = {
  addClass: '',
};
